import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Auto-logout after `idleMinutes` of no keyboard/mouse/touch activity.
 * Only activates for POS-facing roles per the website spec §1 —
 * a 5-minute idle timeout at the register.
 *
 * Not applied to customers (they can browse the storefront indefinitely).
 */
const POS_ROLES = new Set(["owner", "admin", "manager", "associate", "cashier", "driver"]);

export function useIdleLogout({ idleMinutes = 5, testid = "idle-logout" } = {}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const role = user.role || "";
    if (!POS_ROLES.has(role)) return; // customers never auto-logout

    const idleMs = idleMinutes * 60 * 1000;

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        toast.warning(`Signed out after ${idleMinutes} minutes of inactivity.`);
        logout();
        navigate("/login");
      }, idleMs);
    };

    // Any of these events resets the timer
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, idleMinutes, logout, navigate]);
}
